<template>
  <div class="data-table-container">
    <!-- ドラッグモード切り替えトグル -->
    <div class="drag-mode-toggle" v-if="isToggleDisplay">
      <label class="toggle-switch">
        <span class="toggle-label">スクロール</span>
        <div class="toggle-container">
          <input 
            type="checkbox" 
            :checked="isDragModeEnabled" 
            @change="toggleDragMode"
          />
          <span class="toggle-slider"></span>
        </div>
      </label>
    </div>
    
    <!-- テーブル -->
    <div class="table-container">
      <div 
        class="table-scroll-container" 
        ref="tableContainer"
        @mousedown="handleMouseDown"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        :style="{ marginRight: `${calculatedActionColumnWidth}px`, userSelect: (isToggleDisplay && isDragModeEnabled) ? 'none' : 'text' }"
      >
        <table class="table">
          <thead>
            <tr>
              <th 
                v-for="column in visibleColumnsFiltered" 
                :key="column.key" 
                :class="getColumnWidthClass(column)"
                :style="getColumnStyle(column)"
              >
                <slot :name="`header-${column.key}`" :column="column">
                  <div class="column-header">
                    {{ column.label }}
                    <span v-if="column.sortable" class="sort-icon">
                      <i class="fa fa-sort" aria-hidden="true"></i>
                    </span>
                  </div>
                </slot>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-if="0 < paginatedItems.length">
              <tr v-for="(item, index) in paginatedItems" :key="getItemKey(item, index)">
                <td 
                  v-for="column in visibleColumnsFiltered" 
                  :key="column.key" 
                  :class="getColumnWidthClass(column)"
                  :style="[getColumnStyle(column), item.backGroundColorPink ? { backgroundColor: '#fbdada' } : {}]"
                >
                  <slot :name="column.key" :item="item" :value="item[column.key]">
                    {{ formatValue(item[column.key], column.formatter) }}
                  </slot>
                </td>
              </tr>
            </template>
            <tr v-else>
              <td :colspan="visibleColumnsFiltered.length + (actions && 0 < actions.length ? 1 : 0)" class="text-center empty-message">
                {{ emptyMessage }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      

      
      <!-- 固定アクションカラム -->
      <div class="fixed-actions-wrapper" v-if="actions && 0 < actions.length" :style="{ width: `${calculatedActionColumnWidth}px`, right: 0 }">
        <div class="fixed-actions-header">
          <div class="fixed-th">操作</div>
        </div>
        <div class="fixed-actions-body">
          <template v-if="0 < paginatedItems.length">
            <div class="fixed-tr" v-for="(item, index) in paginatedItems" :key="`action-${getItemKey(item, index)}`">
              <div class="fixed-td actions-cell">
                <template v-for="(action, actionIndex) in actions" :key="actionIndex">
                  <button 
                    class="btn btn-sm" 
                    :class="action.buttonClass || 'btn-secondary'" 
                    @click="handleAction(action, item)"
                    :disabled="isActionDisabled(action, item)"
                    :style="{ opacity: isActionDisabled(action, item) ? 0.3 : 1 }"
                  >
                    <i v-if="action.icon" :class="['fa', action.icon]" aria-hidden="true"></i>
                    {{ action.label }}
                  </button>
                </template>
              </div>
            </div>
          </template>
          <div class="fixed-tr empty-row" v-else>
            <div class="fixed-td empty-actions"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- ページネーション (下部) -->
    <div v-if="showPagination && paginationPosition !== 'top'" class="pagination-container">
      <div class="pagination-info" v-if="actualTotalItems > 0" style="flex: 0 0 auto; text-align: left;">
      全 {{ actualTotalItems }}件 {{ currentPage * localPerPage + 1 }} - {{ Math.min((currentPage + 1) * localPerPage, actualTotalItems) }}件表示
      </div>
      <div class="pagination-controls" style="flex: 1 1 auto; text-align: center;">
      <!-- 最初のページへ -->
      <button 
        class="btn btn-sm btn-outline-secondary pagination-btn" 
        :disabled="currentPage <= 0" 
        @click="goToFirstPage()"
        title="最初のページへ"
      >
        &laquo;
      </button>
      
      <!-- 前のページへ -->
      <button 
        class="btn btn-sm btn-outline-secondary pagination-btn" 
        :disabled="currentPage <= 0" 
        @click="changePage(currentPage - 1)"
        title="前のページへ"
      >
        &lsaquo;
      </button>
      
      <!-- ページ番号ボタン -->
      <template v-for="pageNum in displayedPageNumbers" :key="pageNum">
        <button 
        v-if="pageNum !== '...'" 
        class="btn btn-sm pagination-btn" 
        :class="[
          'btn-' + (pageNum == currentPage + 1 ? 'primary' : 'outline-secondary'),
          { 'active': pageNum == currentPage + 1 }
        ]"
        @click="changePage(pageNum - 1)"
        >
        {{ pageNum }}
        </button>
        <span v-else class="pagination-ellipsis">...</span>
      </template>
      
      <!-- 次のページへ -->
      <button 
        class="btn btn-sm btn-outline-secondary pagination-btn" 
        :disabled="currentPage >= totalPages - 1" 
        @click="changePage(currentPage + 1)"
        title="次のページへ"
      >
        &rsaquo;
      </button>
      
      <!-- 最後のページへ -->
      <button 
        class="btn btn-sm btn-outline-secondary pagination-btn" 
        :disabled="currentPage >= totalPages - 1" 
        @click="goToLastPage()"
        title="最後のページへ"
      >
        &raquo;
      </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormListTable',
  props: {
    /**
     * テーブルに表示するデータ配列
     */
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    /**
     * 表示するカラム定義
     * [{ 
     *   key: 'id', 
     *   label: 'ID', 
     *   formatter: (value) => `#${value}`,
     *   minWidth: 120, // カラムの最小幅（px）
     *   maxWidth: 160  // カラムの最大幅（px）
     * }]
     */
    columns: {
      type: Array,
      required: true,
      default: () => []
    },
    /**
     * 操作ボタン定義
     * [{ 
     *   label: '詳細', 
     *   buttonClass: 'btn-secondary', 
     *   icon: 'fa-info-circle', // FontAwesomeアイコン
     *   action: (item) => {}, 
     *   condition: (item) => true // ボタン表示条件
     * }]
     */
    actions: {
      type: Array,
      default: () => []
    },
    /**
     * ページあたりの表示件数
     */
    perPage: {
      type: Number,
      default: 10
    },
    /**
     * ページネーションオプション
     */
    perPageOptions: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    /**
     * 現在のページ番号（1から始まる）
     */
    page: {
      type: Number,
      default: 1
    },
    /**
     * ページネーション表示位置 ('top', 'bottom', 'both')
     */
    paginationPosition: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom', 'both'].includes(value)
    },
    /**
     * ページネーション表示有無
     */
    showPagination: {
      type: Boolean,
      default: true
    },
    /**
     * データが空の場合のメッセージ
     */
    emptyMessage: {
      type: String,
      default: 'データがありません'
    },
    /**
     * 一意のキーとして使用するプロパティ名
     */
    itemKey: {
      type: String,
      default: 'id'
    },
    /**
     * 表示カラム設定
     */
    visibleColumns: {
      type: Array,
      default: () => []
    },
    /**
     * 合計アイテム数（APIから取得した総件数）
     */
    totalItems: {
      type: Number,
      default: null
    },
    /**
     * アクションカラムの幅（px）
     * 指定がない場合は自動計算
     */
    actionColumnWidth: {
      type: Number,
      default: null
    },
    /**
     * ドラッグモード切り替えトグルの表示/非表示
     * falseの場合、ドラッグモードを無効にしてトグルを非表示にする
     */
    isToggleDisplay: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentPage: this.page, // 0-indexedに変換
      localPerPage: 50, // 固定で50件表示
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      isDragModeEnabled: this.isToggleDisplay // トグル表示の設定に応じてドラッグモードを初期化
    }
  },
  computed: {
    /**
     * 実際に使用する合計アイテム数
     */
    actualTotalItems() {
      // APIから取得した総件数があればそれを使用、なければアイテム配列の長さを使用
      return this.$props.totalItems !== null ? this.$props.totalItems : this.items.length;
    },
    /**
     * 合計ページ数
     */
    totalPages() {
      return Math.ceil(this.actualTotalItems / this.localPerPage) || 1;
    },
    /**
     * 表示するページ番号の配列
     * 現在のページを常に中央に表示し、前後に表示するページ番号と省略記号を含む
     */
    displayedPageNumbers() {
      const totalPages = this.totalPages;
      const currentPage = this.currentPage + 1; // 1-indexedに変換
      
      // ページ数が7以下の場合はすべてのページ番号を表示
      if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }
      
      // 常に現在のページを中央に表示するロジック
      // 表示するページ番号の数（省略記号を除く）
      const displayCount = 5;
      const halfCount = Math.floor(displayCount / 2);
      
      // 結果配列
      let result = [];
      
      // 最初のページは常に表示
      result.push(1);
      
      // 現在のページが前方に近い場合
      if (currentPage <= halfCount + 1) {
        // 最初のページから連続して表示
        for (let i = 2; i <= displayCount; i++) {
          result.push(i);
        }
        // 省略記号と最後のページを追加
        result.push('...', totalPages);
      } 
      // 現在のページが後方に近い場合
      else if (currentPage >= totalPages - halfCount) {
        // 省略記号を追加
        result.push('...');
        // 最後のページから逆算して連続表示
        for (let i = totalPages - displayCount + 1; i <= totalPages; i++) {
          if (1 < i) result.push(i);
        }
        // 最後のページは既に追加済み
      } 
      // 現在のページが中央にある場合
      else {
        // 省略記号を追加
        result.push('...');
        // 現在のページの前後に表示
        for (let i = currentPage - halfCount + 1; i <= currentPage + halfCount - 1; i++) {
          result.push(i);
        }
        // 省略記号と最後のページを追加
        result.push('...', totalPages);
      }
      
      return result;
    },
    /**
     * 現在のページの開始インデックス
     */
    startIndex() {
      // APIからのページ番号はそのまま使用する（0-indexed）
      // 実際のページネーションはAPI側で処理されるため、常に0を返す
      return 0;
    },
    /**
     * 現在のページの終了インデックス
     */
    endIndex() {
      const end = this.startIndex + this.localPerPage;
      return Math.min(end, this.actualTotalItems) - (this.actualTotalItems === 0 ? 0 : 1);
    },
    /**
     * 現在のページのアイテム
     */
    paginatedItems() {
      // ページネーションが無効の場合は全アイテムを返す
      if (!this.showPagination) {
        return this.items;
      }
      
      // APIから取得したデータはそのまま表示（ページネーションはAPI側で処理済み）
      return this.items;
    },

    /**
     * 表示するカラム
     */
    visibleColumnsFiltered() {
      if (this.visibleColumns && 0 < this.visibleColumns.length) {
        // 'select'キーのカラムは常に表示し、その他のカラムはvisibleColumnsに含まれるもののみ表示
        const selectColumn = this.columns.find(col => col.key === 'select');
        const filteredColumns = this.columns.filter(col => col.key === 'select' || this.visibleColumns.includes(col.key));
        
        if (selectColumn && !filteredColumns.some(col => col.key === 'select')) {
          return [selectColumn, ...filteredColumns];
        }
        
        return filteredColumns;
      }
      return this.columns;
    },
    /**
     * 選択可能なカラム（選択UIに表示するカラム）
     */
    selectableColumns() {
      // 'select'キーのカラムは表示設定から除外（常に表示）
      return this.columns.filter(column => column.key !== 'select');
    },
    
    /**
     * アクションカラムの幅を計算
     * プロパティで指定された場合はその値を使用し、
     * 指定がない場合はアイコン数に応じて動的に幅を調整
     */
    calculatedActionColumnWidth() {
      // プロパティで指定された場合はその値を使用
      if (this.actionColumnWidth !== null) {
        return this.actionColumnWidth;
      }
      
      // 指定がない場合は従来の計算方法を使用
      if (!this.actions || this.actions.length === 0) return 0;
      // 1アイコンあたり50px、最小幅は60px
      const width = Math.max(60, Math.min(this.actions.length * 50, 150));
      return width;
    }
  },
  watch: {
    totalPages(newPages) {
      // ページ数が減少した場合、現在のページが範囲外なら調整
      if (this.currentPage > newPages - 1) {
        this.currentPage = newPages - 1;
      }
    },
    visibleColumns(newColumns) {
      if (newColumns && 0 < newColumns.length) {
        this.localVisibleColumns = [...newColumns];
      }
    },
    page(newPage) {
      this.currentPage = newPage;
    }
  },
  methods: {
    /**
     * 最初のページに移動
     */
    goToFirstPage() {
      this.changePage(0);
    },
    
    /**
     * 最後のページに移動
     */
    goToLastPage() {
      this.changePage(this.totalPages - 1);
    },
    
    /**
     * アイテムの一意のキーを取得
     */
    getItemKey(item, index) {
      return item[this.itemKey] !== undefined ? item[this.itemKey] : index;
    },
    /**
     * 値をフォーマット
     */
    formatValue(value, formatter) {
      if (formatter && typeof formatter === 'function') {
        return formatter(value);
      }
      return value;
    },
    /**
     * ページを変更
     */
    changePage(page) {
      if (page < 0 || page > this.totalPages - 1) {
        return;
      }
      this.currentPage = page;
      this.$emit('page-change', page + 1); // 1-indexedに変換して親コンポーネントに通知
    },
    /**
     * アクションボタンのクリックハンドラ
     */
    handleAction(action, item) {
      if (action.action && typeof action.action === 'function') {
        action.action(item);
      }
      this.$emit('action', { action: action.key || action.label, item });
    },
    /**
     * アクションボタンの表示判定
     */
    showAction(action, item) {
      if (action.condition && typeof action.condition === 'function') {
        return action.condition(item);
      }
      return true;
    },
    
    /**
     * アクションボタンの無効化判定
     */
    isActionDisabled(action, item) {
      if (action.disabled && typeof action.disabled === 'function') {
        return action.disabled(item);
      }
      return false;
    },
    /**
     * カラム幅のスタイルを取得
     */
    getColumnWidthClass(column) {
      // 後方互換性のためにwidthLevelもサポート
      if (column.widthLevel) {
        return `col-width-${column.widthLevel}`;
      }
      
      // 新しいminWidth, maxWidthプロパティを使用
      return '';
    },
    
    /**
     * カラム幅のインラインスタイルを取得
     */
    getColumnStyle(column) {
      const style = {};
      
      if (column.minWidth) {
        style.minWidth = `${column.minWidth}px`;
      }
      
      if (column.maxWidth) {
        style.maxWidth = `${column.maxWidth}px`;
        style.overflow = 'hidden';
        style.textOverflow = 'ellipsis';
        style.whiteSpace = 'nowrap';
      }
      
      return style;
    },
    

    /**
     * ドラッグモードの切り替え
     */
    toggleDragMode() {
      // トグルが非表示の場合は切り替えを無効にする
      if (!this.isToggleDisplay) {
        return;
      }
      this.isDragModeEnabled = !this.isDragModeEnabled;
      // カーソルスタイルを更新
      this.$refs.tableContainer.style.cursor = this.isDragModeEnabled ? 'default' : 'auto';
    },
    
    /**
     * マウスダウンイベントハンドラ
     */
    handleMouseDown(e) {
      // トグルが非表示の場合またはドラッグモードが無効の場合はドラッグを無効にする
      if (!this.isToggleDisplay || !this.isDragModeEnabled) {
        return;
      }
      this.startDrag(e);
    },
    
    /**
     * ドラッグ開始
     */
    startDrag(e) {
      // トグルが非表示の場合またはドラッグモードが無効の場合はドラッグを無効にする
      if (!this.isToggleDisplay || !this.isDragModeEnabled) return;
      
      this.isDragging = true;
      this.startX = e.pageX - this.$refs.tableContainer.offsetLeft;
      this.scrollLeft = this.$refs.tableContainer.scrollLeft;
      this.$refs.tableContainer.style.cursor = 'grabbing';
      // デフォルトのテキスト選択を防止
      e.preventDefault();
    },
    /**
     * ドラッグ中
     */
    onDrag(e) {
      // トグルが非表示の場合またはドラッグモードが無効の場合、または現在ドラッグ中でない場合はドラッグを無効にする
      if (!this.isToggleDisplay || !this.isDragging || !this.isDragModeEnabled) return;
      const x = e.pageX - this.$refs.tableContainer.offsetLeft;
      const walk = (x - this.startX) * 2; // スクロール速度を調整
      this.$refs.tableContainer.scrollLeft = this.scrollLeft - walk;
    },
    /**
     * ドラッグ終了
     */
    stopDrag() {
      this.isDragging = false;
      this.$refs.tableContainer.style.cursor = 'default';
    },
    

  }
}
</script>

<style scoped>
.data-table-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--gray-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 49px;
  height: 49px;
  box-sizing: border-box;
}

.table th {
  font-weight: bold;
  background-color: #195983;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 49px;
  height: 49px;
  box-sizing: border-box;
}

.table th:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.table th:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* テーブルコンテナ */
.table-container {
  position: relative;
  margin-bottom: 1rem;
  overflow: visible; /* Changed from hidden to visible to prevent cutting off fixed elements */
}

/* ドラッグモード切り替えトグル */
.drag-mode-toggle {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 10px;
}

/* トグルスイッチのスタイル */
.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.toggle-label {
  margin-right: 8px;
  font-size: 14px;
}

.toggle-container {
  position: relative;
  width: 40px;
  height: 20px;
}

.toggle-container input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 34px;
  transition: .4s;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: .4s;
}

input:checked + .toggle-slider {
  background-color: #4CD964;
}

input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

/* スクロール可能なテーブル部分 */
.table-scroll-container {
  overflow-x: auto;
  cursor: default;
  max-width: 100%;
  position: relative; /* Added to establish a positioning context */
}

/* 操作カラムのプレースホルダー（非表示） */
.actions-column-placeholder {
  width: 0;
  padding: 0;
  visibility: hidden;
}

/* 固定アクションカラム */
.fixed-actions-wrapper {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 150px; /* 最大3アイコンが入るよう幅を拡大 */
  background-color: white;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: flex;
  flex-direction: column;
  border-left: 1px solid var(--gray-medium);
  pointer-events: auto; /* Ensure clicks are captured */
  overflow: visible; /* Ensure the wrapper is not cut off */
}

.fixed-actions-header {
  background-color: #195983;
  color: white;
  font-weight: bold;
  height: 49px; /* メインテーブルのヘッダーと同じ高さ */
  box-sizing: border-box;
  border-bottom: 1px solid var(--gray-medium);
}

.fixed-th {
  padding: 0.75rem;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fixed-actions-body {
  overflow-y: hidden;
  flex: 1;
}

.fixed-tr {
  border-bottom: 1px solid var(--gray-medium);
  height: 49px; /* メインテーブルの行と同じ高さ */
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.fixed-td {
  padding: 0.75rem;
  background-color: white;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 100%;
  box-sizing: border-box;
}

.empty-actions {
  height: 3.5rem; /* 空の場合の高さ調整 */
}

.empty-row {
  height: 53px;
}

/* 行の高さを揃えるためのスタイル */
.table tr, .fixed-tr {
  box-sizing: border-box;
  height: 49px; /* 両方のテーブルで統一した高さを使用 */
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.pagination-info {
  color: var(--gray-dark, #6c757d);
  padding: 0.25rem 0.5rem;
  flex: 1;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 2;
  justify-content: center;
}

.pagination-page {
  padding: 0 0.5rem;
}

.pagination-btn {
  min-width: 40px;
  margin: 0 2px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.pagination-ellipsis {
  min-width: 40px;
  margin: 0 2px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
}

.pagination-btn.active {
  font-weight: bold;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.actions-cell {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.empty-message {
  padding: 1.5rem;
  font-style: italic;
  color: var(--gray-dark, #6c757d);
}

/* カラム幅の定義 - 後方互換性のため */
.col-width-2xs {
  min-width: 40px;
  max-width: 60px;
}

.col-width-xs {
  min-width: 60px;
  max-width: 90px;
}

.col-width-sm {
  min-width: 90px;
  max-width: 120px;
}

.col-width-md {
  min-width: 120px;
  max-width: 160px;
}

.col-width-lg {
  min-width: 160px;
  max-width: 220px;
}

.col-width-xl {
  min-width: 200px;
  max-width: 280px;
}

.col-width-2xl {
  min-width: 250px;
  max-width: 350px;
}

.col-width-3xl {
  min-width: 300px;
  max-width: 450px;
}


</style>
